import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Paperclip, FileText, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { FadeUp } from './Motion';

const Attachment = ({ msg }) => {
  const open = async () => {
    try {
      const res = await api.get(`/support/attachment/${msg.id}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Fichier indisponible'); }
  };
  return (
    <button onClick={open} className="flex items-center space-x-2 mt-1.5 text-xs font-semibold underline opacity-90 hover:opacity-100">
      <FileText className="w-3.5 h-3.5" /><span className="truncate max-w-[180px]">{msg.attachment.filename}</span>
    </button>
  );
};

export const AdminSupport = () => {
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try { const r = await api.get('/admin/support/threads'); setThreads(r.data.threads || []); } catch {}
  }, []);

  const loadMessages = useCallback(async (userId) => {
    try { const r = await api.get(`/admin/support/threads/${userId}`); setMessages(r.data.messages || []); } catch {}
  }, []);

  useEffect(() => {
    loadThreads();
    const iv = setInterval(() => { loadThreads(); if (sel) loadMessages(sel.user_id); }, 6000);
    return () => clearInterval(iv);
  }, [loadThreads, loadMessages, sel]);

  useEffect(() => { if (sel) loadMessages(sel.user_id); }, [sel, loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!sel || (!text.trim() && !file)) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('text', text.trim());
      if (file) fd.append('file', file);
      await api.post(`/admin/support/threads/${sel.user_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setText(''); setFile(null);
      loadMessages(sel.user_id); loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  return (
    <FadeUp className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] overflow-hidden" data-testid="admin-support-section">
      <div className="grid grid-cols-1 md:grid-cols-3 min-h-[560px]">
        {/* Threads list */}
        <div className="border-r border-gray-100 overflow-y-auto max-h-[560px]">
          <div className="p-4 border-b border-gray-100 font-extrabold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Conversations ({threads.length})
          </div>
          {threads.length === 0 && <div className="p-6 text-sm text-gray-400 text-center">Aucune conversation</div>}
          {threads.map((th) => (
            <button key={th.user_id} onClick={() => setSel(th)} data-testid={`thread-${th.user_id}`}
              className={`w-full text-left p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${sel?.user_id === th.user_id ? 'bg-gray-50' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-gray-900 truncate">{th.user_email}</span>
                {th.unread > 0 && <span className="bg-rose-500 text-white text-[10px] font-extrabold rounded-full px-2 py-0.5 shrink-0" data-testid={`thread-unread-${th.user_id}`}>{th.unread}</span>}
              </div>
              <div className="text-xs text-gray-400 truncate mt-0.5">{th.last_sender === 'support' ? 'Vous : ' : ''}{th.last_text || '📎 Pièce jointe'}</div>
              <div className="text-[10px] text-gray-300 mt-0.5">{new Date(th.last_at).toLocaleString('fr-FR')}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="md:col-span-2 flex flex-col max-h-[560px]">
          {!sel ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
              <MessageCircle className="w-12 h-12 mb-3" />
              <p className="text-sm">Sélectionnez une conversation</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center space-x-3">
                <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-extrabold text-sm">{sel.user_email[0].toUpperCase()}</div>
                <div>
                  <div className="font-bold text-sm text-gray-900">{sel.user_email}</div>
                  <div className="text-[11px] text-gray-400">Le client voit vos messages comme « Support alphaswapp »</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F6F7]" data-testid="admin-chat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'support' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 text-sm shadow-sm ${m.sender === 'support'
                      ? 'bg-gray-900 text-white rounded-2xl rounded-br-md' : 'bg-white text-gray-900 rounded-2xl rounded-bl-md'}`}>
                      {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                      {m.attachment && <Attachment msg={m} />}
                      <div className={`text-[10px] mt-1 ${m.sender === 'support' ? 'text-white/50' : 'text-gray-400'}`}>
                        {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {file && (
                <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center justify-between text-xs">
                  <span className="truncate font-semibold text-amber-800">📎 {file.name}</span>
                  <button onClick={() => setFile(null)} className="text-amber-600 font-bold ml-2">✕</button>
                </div>
              )}
              <div className="p-3 border-t border-gray-100 flex items-center space-x-2">
                <label className="p-2.5 rounded-full hover:bg-gray-100 cursor-pointer transition-colors shrink-0">
                  <Paperclip className="w-4 h-4 text-gray-500" />
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files[0] && setFile(e.target.files[0])} data-testid="admin-chat-file-input" />
                </label>
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                  placeholder="Répondre en tant que Support alphaswapp..." className="flex-1 bg-[#F5F6F7] rounded-full px-4 py-2.5 text-sm outline-none" data-testid="admin-chat-input" />
                <button onClick={send} disabled={sending || (!text.trim() && !file)}
                  className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40 shrink-0" data-testid="admin-chat-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </FadeUp>
  );
};

export default AdminSupport;
